
import React, { useState, useEffect } from 'react';
import { SubjectType, UserProfile, Language } from '../types';
import { translations } from '../i18n';
import { Search, Zap, Compass, Clock, ArrowRight, MapPin, Sparkles, Flame, Calendar, Rocket } from 'lucide-react';

interface HomePageProps {
  user: UserProfile;
  lang: Language;
  onSubjectSelect: (s: SubjectType) => void;
  onStartAdventure: (topic: string) => void;
  onContinue: () => void;
}

// --- CURATED LIBRARY (High-Key, Vibrant Prompts) ---
const ADVENTURE_LIBRARY = [
  {
    id: 'daily_1',
    topic: { en: 'Secrets of the Pyramids', zh: '金字塔的未解之谜' },
    subject: 'history' as SubjectType,
    prompt: 'Hyper-realistic view of Great Pyramids, golden sunset, cinematic lighting, national geographic photography, sharp details, blue and orange sky, 8k',
    color: 'from-amber-400 to-orange-500',
    tag: { en: 'Rare', zh: '稀有' }
  },
  {
    id: 'daily_2',
    topic: { en: 'Microscopic Life', zh: '一滴水里的微观宇宙' },
    subject: 'science' as SubjectType,
    prompt: 'Stunning microscopic world in a water drop, vibrant bioluminescent organisms, glowing particles, deep focus, crystal clear, macro photography',
    color: 'from-cyan-400 to-teal-500',
    tag: { en: 'Nature', zh: '自然' }
  },
  {
    id: 'daily_3',
    topic: { en: 'Cyberpunk Logic', zh: '赛博城市的逻辑电路' },
    subject: 'math' as SubjectType,
    prompt: 'Vibrant futuristic city, neon circuit patterns, clean aesthetics, sunshine through glass buildings, architectural photography, bright and clear',
    color: 'from-blue-400 to-indigo-500',
    tag: { en: 'Logic', zh: '逻辑' }
  },
  {
    id: 'daily_4',
    topic: { en: 'Van Gogh\'s Starry Night', zh: '梵高的星空梦境' },
    subject: 'art' as SubjectType,
    prompt: 'Vivid oil painting of a starry sky, van gogh style, rich impasto, swirling bright yellow stars, vibrant blue night, masterpiece',
    color: 'from-blue-400 to-yellow-500',
    tag: { en: 'Art', zh: '美学' }
  }
];

const getFeaturedImage = (prompt: string, id: string) => {
    return `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?width=800&height=450&nologo=true&seed=${id}&model=flux`;
};

export const HomePage: React.FC<HomePageProps> = ({ 
  user, lang, onSubjectSelect, onStartAdventure, onContinue 
}) => {
  const t = translations[lang];
  const [topic, setTopic] = useState("");
  const [dailyMission, setDailyMission] = useState(ADVENTURE_LIBRARY[0]);

  useEffect(() => {
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
    const index = dayOfYear % ADVENTURE_LIBRARY.length;
    setDailyMission(ADVENTURE_LIBRARY[index]);
  }, []);

  const handleLaunch = () => {
    if (topic.trim()) onStartAdventure(topic);
  };

  const handleFeaturedClick = () => {
    const topicText = lang === 'zh' ? dailyMission.topic.zh : dailyMission.topic.en;
    onStartAdventure(topicText);
  };

  return (
    <div className="flex flex-col items-center animate-in fade-in slide-in-from-bottom-8 duration-700">
      
      {/* 1. HERO TITLE */}
      <div className="text-center mt-8 md:mt-16 mb-12 relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-indigo-200/30 dark:bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none"></div>
          <h1 className="text-4xl md:text-6xl font-black font-orbitron text-transparent bg-clip-text bg-gradient-to-b from-slate-900 via-indigo-700 to-indigo-500 dark:from-white dark:via-indigo-100 dark:to-indigo-300 whitespace-pre-line leading-tight">
            {t.exploreUniverse}
          </h1>
      </div>

      {/* 2. SUPER SEARCH BAR */}
      <div className="w-full max-w-3xl relative mb-16 group z-20 px-4">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-violet-500 rounded-[2.5rem] blur-2xl opacity-10 group-hover:opacity-20 transition-opacity duration-500"></div>
          <div className="relative bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl rounded-[2.5rem] p-2.5 flex items-center shadow-2xl border border-white dark:border-white/10">
            <div className="pl-6 pr-4 text-indigo-500">
                <Search size={28} />
            </div>
            <input 
                className="flex-1 bg-transparent text-slate-800 dark:text-slate-100 placeholder:text-slate-400 font-bold text-lg md:text-xl py-4 focus:outline-none"
                placeholder={t.warpPlaceholder}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLaunch()}
            />
            <button 
                onClick={handleLaunch}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 md:px-10 py-4 rounded-[2rem] font-black text-lg transition-all hover:scale-105 active:scale-95 shadow-lg shadow-indigo-500/30 flex items-center gap-2"
            >
                <Zap size={20} className="fill-white"/> <span className="hidden md:inline">{t.launch}</span>
            </button>
          </div>
      </div>

      {/* 3. SUBJECT CHIPS */}
      <div className="w-full max-w-5xl mb-16 relative z-10 px-4">
          <div className="flex justify-center flex-wrap gap-4">
              <button 
                onClick={onContinue} 
                className="px-6 py-3.5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/10 hover:border-indigo-300 dark:hover:border-indigo-500 text-slate-700 dark:text-white font-black flex items-center gap-2 transition-all hover:-translate-y-1 shadow-md hover:shadow-xl"
              >
                <Compass size={18} className="text-indigo-500" /> {t.allSubjectsBtn}
              </button>
              {(['science', 'english', 'math', 'history', 'art'] as SubjectType[]).map(s => {
                const icons: Record<string, string> = { math: '🔢', chinese: '📜', english: '🔤', science: '🔬', history: '🏛️', art: '🎨' };
                return (
                    <button 
                      key={s}
                      onClick={() => onSubjectSelect(s)}
                      className="px-6 py-3.5 rounded-2xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-black flex items-center gap-3 transition-all hover:-translate-y-1 shadow-md hover:shadow-xl border border-white dark:border-white/10"
                    >
                      <span className="text-xl">{icons[s]}</span> {t.subjects[s]}
                    </button>
                )
              })}
          </div>
      </div>

      {/* 4. UPGRADED DUAL CARDS */}
      <div className="w-full max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10 px-4 md:px-8">
          
          {/* Card 1: CONTINUE JOURNEY (Clean Glass Style) */}
          <div 
            onClick={onContinue}
            className="group relative h-72 rounded-[3rem] overflow-hidden cursor-pointer border-4 border-white dark:border-white/5 shadow-2xl transition-all hover:scale-[1.02]"
          >
              <img 
                src="https://images.unsplash.com/photo-1614732414444-096e5f1122d5?q=80&w=1200&auto=format&fit=crop" 
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                alt="Galaxy"
              />
              {/* Vibrant Gradient Overlay instead of dark */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/60 via-transparent to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
              
              <div className="absolute inset-0 p-10 flex flex-col justify-between">
                 <div>
                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-white font-black uppercase tracking-widest text-[10px] border border-white/30 mb-4">
                       <Clock size={12} className="animate-spin-slow"/> {t.continueJourney}
                    </div>
                    <h3 className="text-4xl font-black text-white leading-tight mb-2 drop-shadow-md">
                       {lang === 'zh' ? '星际导航术' : 'Star Navigation'}
                    </h3>
                    <div className="flex items-center gap-2 text-indigo-100 text-sm font-bold bg-indigo-500/30 w-fit px-3 py-1 rounded-lg backdrop-blur-sm">
                       <MapPin size={14}/> {t.subjects.math} • Level 1-2
                    </div>
                 </div>

                 <div className="max-w-[280px]">
                    <div className="flex justify-between text-xs font-black text-white mb-2 uppercase tracking-wider">
                       <span>{lang === 'zh' ? '当前进度' : 'Progress'}</span>
                       <span>60%</span>
                    </div>
                    <div className="h-2.5 w-full bg-white/20 rounded-full overflow-hidden backdrop-blur-md border border-white/10">
                       <div className="h-full bg-gradient-to-r from-indigo-400 to-white w-[60%] shadow-[0_0_15px_rgba(255,255,255,0.8)]"></div>
                    </div>
                    <div className="mt-5 flex items-center gap-2 text-white font-black text-sm group-hover:translate-x-2 transition-transform">
                       {t.resume} <ArrowRight size={18}/>
                    </div>
                 </div>
              </div>
          </div>

          {/* Card 2: DAILY DISCOVERY (Vibrant Real Image Style) */}
          <div 
            onClick={handleFeaturedClick}
            className="group relative h-72 rounded-[3rem] overflow-hidden cursor-pointer border-4 border-white dark:border-white/5 shadow-2xl transition-all hover:scale-[1.02]"
          >
              {/* No heavy blend, pure high-quality image */}
              <img 
                src={getFeaturedImage(dailyMission.prompt, dailyMission.id)}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                alt="Daily Pick"
              />
              
              {/* Soft vignette only at bottom for text readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>

              {/* High-Contrast Badge */}
              <div className="absolute top-8 right-8">
                 <div className="bg-orange-500 text-white px-5 py-2 rounded-2xl text-xs font-black shadow-2xl flex items-center gap-2 border-2 border-white animate-bounce-slow">
                    <Flame size={16} className="fill-white" />
                    {lang === 'zh' ? dailyMission.tag.zh : dailyMission.tag.en}
                 </div>
              </div>

              <div className="absolute bottom-0 left-0 w-full p-10">
                <div className="flex items-center gap-2 mb-3">
                    <div className="bg-yellow-400 p-2.5 rounded-2xl shadow-lg border-2 border-white">
                        <Calendar size={20} className="text-slate-900"/>
                    </div>
                    <span className="text-xs font-black uppercase tracking-[0.2em] text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                        {lang === 'zh' ? '每日精选' : 'Daily Pick'}
                    </span>
                </div>
                <h3 className="text-3xl font-black text-white leading-tight drop-shadow-2xl mb-1">
                    {lang === 'zh' ? dailyMission.topic.zh : dailyMission.topic.en}
                </h3>
                <p className="text-white/90 text-sm font-bold drop-shadow-md">
                    {t.subjects[dailyMission.subject]} • {lang === 'zh' ? '开启沉浸式探险' : 'Start Immersive Mission'}
                </p>
              </div>
          </div>

      </div>

      {/* Decorative Stars for Home */}
      <div className="mt-16 text-center opacity-20 dark:opacity-40">
          <Rocket className="text-indigo-400 inline-block animate-float" size={32} />
      </div>
    </div>
  );
};
